// src/utils/shareUtils.js
import { getCountryName } from './countries';

// URL base de la aplicación
const BASE_URL = 'https://prayer-app-flax.vercel.app';

// Generar URL única para un pedido
export const generatePrayerUrl = (prayerId) => {
  return `${BASE_URL}/oracion/${prayerId}`;
};

// Generar texto para compartir
export const generateShareText = (prayer) => {
  const countryName = getCountryName(prayer.pais);
  return `🙏 Pedido de Oración de ${prayer.nombre} desde ${countryName}

"${prayer.comentario}"

Categoría: ${prayer.categoria}
Únete en oración: ${generatePrayerUrl(prayer.id)}

#PedidosDeOracion #Fe #Esperanza`;
};

// Generar texto corto para WhatsApp
export const generateWhatsAppText = (prayer) => {
  const countryName = getCountryName(prayer.pais);
  return `🙏 Hice un pedido de oración para que más personas pidan por esta intención:

"${prayer.comentario}" - ${prayer.nombre} (${countryName})

${generatePrayerUrl(prayer.id)}

También puedes pedir que oren por tu intención ✨`;
};

// Verificar si el dispositivo soporta Web Share API
export const canUseNativeShare = () => {
  return navigator.share && navigator.canShare;
};

// Compartir usando Web Share API nativa
export const shareNative = async (prayer) => {
  if (!canUseNativeShare()) {
    throw new Error('Web Share API no disponible');
  }

  const shareData = {
    title: `Pedido de Oración - ${prayer.nombre}`,
    text: generateShareText(prayer),
    url: generatePrayerUrl(prayer.id)
  };

  try {
    await navigator.share(shareData);
    return true;
  } catch (error) {
    if (error.name !== 'AbortError') {
      throw error;
    }
    return false; // Usuario canceló
  }
};

// Copiar texto al portapapeles
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    // Fallback para navegadores sin soporte
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (fallbackError) {
      document.body.removeChild(textArea);
      throw fallbackError;
    }
  }
};

// Compartir en WhatsApp
export const shareWhatsApp = (prayer) => {
  const text = generateWhatsAppText(prayer);
  const encodedText = encodeURIComponent(text);
  const whatsappUrl = `https://wa.me/?text=${encodedText}`;
  
  // Abrir en nueva ventana
  window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
};

// Compartir en Facebook
export const shareFacebook = (prayer) => {
  const url = generatePrayerUrl(prayer.id);
  const encodedUrl = encodeURIComponent(url);
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  
  window.open(facebookUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
};

// Compartir en Twitter
export const shareTwitter = (prayer) => {
  const text = generateShareText(prayer);
  const encodedText = encodeURIComponent(text);
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
  
  window.open(twitterUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
};

// Función principal de compartir con múltiples opciones
export const handleShare = async (prayer, method = 'auto') => {
  try {
    switch (method) {
      case 'auto':
        // Intentar compartir nativo primero
        if (canUseNativeShare()) {
          return await shareNative(prayer);
        } else {
          // Fallback: mostrar opciones
          return 'show-options';
        }
        
      case 'native':
        return await shareNative(prayer);
        
      case 'copy-link':
        const url = generatePrayerUrl(prayer.id);
        return await copyToClipboard(url);
        
      case 'copy-text':
        const text = generateShareText(prayer);
        return await copyToClipboard(text);
        
      case 'whatsapp':
        shareWhatsApp(prayer);
        return true;
        
      case 'facebook':
        shareFacebook(prayer);
        return true;
        
      case 'twitter':
        shareTwitter(prayer);
        return true;
        
      default:
        throw new Error('Método de compartir no válido');
    }
  } catch (error) {
    console.error('Error al compartir:', error);
    throw error;
  }
};

// Opciones de compartir disponibles
export const getShareOptions = () => [
  {
    id: 'native',
    label: 'Compartir',
    icon: '📱',
    available: canUseNativeShare(),
    primary: true
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: '💬',
    available: true,
    primary: true
  },
  {
    id: 'copy-link',
    label: 'Copiar enlace',
    icon: '🔗',
    available: true,
    primary: true
  },
  {
    id: 'copy-text',
    label: 'Copiar texto',
    icon: '📄',
    available: true,
    primary: false
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: '📘',
    available: true,
    primary: false
  },
  {
    id: 'twitter',
    label: 'Twitter',
    icon: '🐦',
    available: true,
    primary: false
  }
];